import fetchLoyaltyActionRewards from 'actions/loyalty/fetchLoyaltyActionRewards'
import { useAppDispatch } from 'hooks/reduxHooks'
import useDidChange from 'hooks/useDidChange'
import useTimeout from 'hooks/useTimeout'
import type { SupportedActionReward } from 'loyalty/constants/loyaltyActionRewards'
import { useCallback, useEffect, useRef, useState } from 'react'

const ACTION_REWARD_POLL_DELAYS_MS = [2_000, 5_000, 15_000]

/**
 * Fetch action rewards immediately, then twice more unless the completion key changes.
 * */
function useLoyaltyActionRewardPolling(completionKey: unknown) {
  const dispatch = useAppDispatch()
  const completionKeyChanged = useDidChange(completionKey, completionKey)
  const { set: setRetryTimer, clear: clearRetryTimer } = useTimeout()

  const isPollingRef = useRef(false)
  const consecutiveRetryCountRef = useRef(0)
  const [pendingActionNames, setPendingActionNames] = useState<ReadonlySet<SupportedActionReward>>(new Set())

  const startPolling = useCallback((actionName: SupportedActionReward) => {
    if (isPollingRef.current) return

    isPollingRef.current = true
    setPendingActionNames(new Set([actionName]))
    consecutiveRetryCountRef.current = 0
    dispatch(fetchLoyaltyActionRewards({ force: true }))

    const scheduleRetry = () => {
      const delayMs = ACTION_REWARD_POLL_DELAYS_MS[consecutiveRetryCountRef.current]
      if (delayMs === undefined) {
        isPollingRef.current = false
        setPendingActionNames(new Set())
        return
      }

      consecutiveRetryCountRef.current += 1
      setRetryTimer(() => {
        if (!isPollingRef.current) return
        dispatch(fetchLoyaltyActionRewards({ force: true }))
        scheduleRetry()
      }, delayMs)
    }

    scheduleRetry()
  }, [dispatch, setRetryTimer])

  const cancelPolling = useCallback(() => {
    isPollingRef.current = false
    setPendingActionNames(new Set())
    clearRetryTimer()
  }, [clearRetryTimer])

  useEffect(() => {
    if (isPollingRef.current && completionKeyChanged) {
      cancelPolling()
    }
  }, [cancelPolling, completionKeyChanged])

  return { pendingActionNames, startPolling }
}

export default useLoyaltyActionRewardPolling
